import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    useScreenInfo,
    useSearch,
} from '@moniebook-web/components/shared/hooks';
import {
    useCustomerAnalytics,
    useGetApiService,
    useSetCurrentBusinessApi,
    useUserInfo,
} from '@moniebook-web/utils';

import {
    ANALYTIC_EVENTS,
    BRAZE_EVENTS,
    HOST_API_URL,
} from '@/shared/constants';
import { QUERY_KEY } from '@/shared/query-key';

import type { BusinessListItem, UseBusinessSwitcherParams } from '../types';
import { sortedBusinessesBySelected } from './helpers';

export const useBusinessSwitcher = ({ onClose }: UseBusinessSwitcherParams) => {
    const { currentBusiness, canCreateBusiness } = useUserInfo();
    const { isMobile } = useScreenInfo();
    const { sendBrazeEvent, sendMixpanelEvent } = useCustomerAnalytics();

    const [oldName, setOldName] = useState<string>();
    const [newBusinessName, setNewBusinessName] = useState<string>();

    const {
        mutate: switchBusiness,
        isPending,
        progress,
        isSuccess,
        reset,
    } = useSetCurrentBusinessApi();

    const { data: businessListData, loading: isBusinessListLoading } =
        useGetApiService<BusinessListItem[]>({
            queryName: QUERY_KEY.GET_BUSINESS_LIST,
            url: HOST_API_URL.GET_BUSINESS_LIST,
            autoExecute: true,
            cacheTime: 0,
        });

    const mappedBusinesses = useMemo(
        () =>
            (businessListData ?? []).map((item) => ({
                id: item.businessId,
                name: item.businessName,
                numberOfBranches: item.branchCount,
            })),
        [businessListData],
    );

    const businessData = useMemo(
        () => sortedBusinessesBySelected(mappedBusinesses, currentBusiness),
        [mappedBusinesses, currentBusiness],
    );

    const { handleSearch, search, searchRef, searchResults } =
        useSearch(businessData);

    const handleOnSelect = useCallback(
        (businessId: string, name: string) => {
            setOldName(currentBusiness?.name);
            setNewBusinessName(name);
            switchBusiness({ businessId, newBusinessName: name });
        },
        [currentBusiness?.name, switchBusiness],
    );

    const handleCreateBusiness = useCallback(() => {
        sendBrazeEvent({ eventName: BRAZE_EVENTS.CREATE_BUSINESS_CLICKED });
        sendMixpanelEvent({
            eventName: ANALYTIC_EVENTS.VIEW_CREATE_BUSINESS_FORM,
        });
        onClose();
    }, [sendBrazeEvent, sendMixpanelEvent, onClose]);

    const switching = isPending || Boolean(oldName && newBusinessName);

    useEffect(() => {
        if (!isSuccess) return;

        onClose();
        reset();
        setTimeout(() => {
            setNewBusinessName(undefined);
            setOldName(undefined);
        }, 1000);
    }, [isSuccess, onClose, reset]);

    return {
        newBusinessName,
        search,
        searchRef,
        searchResults,
        currentBName: switching ? '' : currentBusiness?.name,
        switching,
        isBusinessListLoading,
        businessData,
        progress,
        oldName,
        canCreateBusiness,
        isMobile,
        handleOnSelect,
        handleSearch,
        handleCreateBusiness,
        handleClose: onClose,
        handleReset: reset,
    };
};
